import { upperCaseFirstLetter } from '@zorro/common-utils';
import { $enum } from 'ts-enum-util';

import { PaymentHandlerRole } from './PaymentStatusUtils';

export type PaymentNoteInfoProps = {
  paymentHandlerRole: PaymentHandlerRole;
  enrollmentTeamName?: string | null;
  opsCardLastFour?: string;
};

// Shown instead of the who-pays text while impersonating, for every handler role.
export const IMPERSONATION_DISCLAIMER_TEXT =
  'Card details are hidden during impersonation and joint sessions. The employee must log in directly to view them.';

// The note text shown alongside a card: who pays it normally, or the
// impersonation disclaimer when isImpersonated is true — the single
// decision point for what goes in that slot.
export const getPaymentNoteText = (
  {
    paymentHandlerRole,
    enrollmentTeamName,
    opsCardLastFour,
  }: PaymentNoteInfoProps,
  isImpersonated: boolean
): string => {
  if (isImpersonated) {
    return IMPERSONATION_DISCLAIMER_TEXT;
  }

  const enrollmentTeamLabel = enrollmentTeamName || 'your enrollment team';
  const capitalizedEnrollmentTeamLabel =
    upperCaseFirstLetter(enrollmentTeamLabel);

  return $enum.mapValue(paymentHandlerRole).with({
    [PaymentHandlerRole.EMPLOYEE]:
      'This card is funded by your employer and covers your allowance plus any self-contributions. Use it only for premium payments to your carrier.',
    [PaymentHandlerRole.AGENT]: `${capitalizedEnrollmentTeamLabel} pays your carrier premiums using this card. Nothing needed from you.`,
    [PaymentHandlerRole.SPLIT]: opsCardLastFour
      ? `This card is yours for premium payments. ${capitalizedEnrollmentTeamLabel} covers part of your payments with a card ending ${opsCardLastFour}, which may appear in your carrier's portal.`
      : `This card is yours for premium payments. ${capitalizedEnrollmentTeamLabel} covers part of your payments with a separate card, which may appear in your carrier's portal.`,
  });
};
